import { useSelector } from 'react-redux';
import type { RootState } from '@/app/store';

/**
 * Workspace URL builder — single source of truth for `/workspace/{tenantCode}`
 * route construction (spec: workspace-tenant-code-routing).
 *
 * The active tenant lives in the URL path. Every workspace route is tenant-
 * scoped: `/workspace/{tenantCode}`, `/workspace/{tenantCode}/apps/{appCode}`,
 * etc. No caller should hardcode the `/workspace` literal — route through
 * `workspaceHref` / `useWorkspaceHref` so the tenant code is always carried.
 */

const WORKSPACE_BASE = '/workspace';

/**
 * One-shot `sessionStorage` flag. Set by the tenant workspace layout when it
 * redirects away from an invalid or unauthorized tenant code; read and cleared
 * by `/main` to show the "workspace unavailable" notice (Req 4.3).
 */
export const INVALID_TENANT_NOTICE_KEY = 'sm_workspace_invalid_tenant';

/**
 * Build a workspace URL for a tenant.
 *
 * - With a tenant → `/workspace/{tenantCode}{subPath}` (no trailing slash).
 * - With no tenant → bare `/workspace` (the redirect resolver). Callers that
 *   need a no-tenant destination should target `/main` instead — bare
 *   `/workspace` only ever redirects.
 *
 * `subPath` is tolerant of a leading slash and is never allowed to introduce a
 * trailing slash, e.g. `workspaceHref(t, 'apps/x')` and
 * `workspaceHref(t, '/apps/x')` both yield `/workspace/{code}/apps/x`.
 */
export function workspaceHref(
  tenant: { tenantCode: string } | null | undefined,
  subPath = '',
): string {
  if (!tenant?.tenantCode) return WORKSPACE_BASE;
  const trimmed = subPath.replace(/^\/+/, '').replace(/\/+$/, '');
  const sub = trimmed ? `/${trimmed}` : '';
  return `${WORKSPACE_BASE}/${tenant.tenantCode}${sub}`;
}

/**
 * Hook form of {@link workspaceHref} — reads `activeTenant` from Redux and
 * returns a builder bound to it.
 */
export function useWorkspaceHref(): (subPath?: string) => string {
  const activeTenant = useSelector((s: RootState) => s.tenant.activeTenant);
  return (subPath = '') => workspaceHref(activeTenant, subPath);
}
